import { useCallback, useEffect, useRef, useState } from "react";
import type { ApprovalStage, ShiftProductionFormListItem } from "../../../shared/shift-production-form/contracts";
import { getShiftFormList } from "../shift-production-form/shift-production-form-gateway";
import { loadApprovalFormPages } from "./load-approval-form-pages";

const batchSize = 6;
const currentDay = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Istanbul" }).format(new Date());

export function useTodayApprovedForms(stage: ApprovalStage, actorId: string, refreshVersion: number) {
  const [items, setItems] = useState<ShiftProductionFormListItem[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const [error, setError] = useState<string | null>(null);
  const requestVersion = useRef(0);
  const day = useRef(currentDay());
  const reload = useCallback(async () => {
    const request = ++requestVersion.current;
    setPhase("loading");
    setError(null);
    try {
      const result = await loadApprovalFormPages(
        offset => getShiftFormList("processed", { stage, approvedToday: true, limit: batchSize, offset }),
        () => request === requestVersion.current,
      );
      if (!result || request !== requestVersion.current) return;
      setItems(result.items);
      setTotal(result.total);
      setPhase("ready");
    } catch (reason) {
      if (request !== requestVersion.current) return;
      setError(reason instanceof Error ? reason.message : "Bugünkü onaylar yüklenemedi.");
      setPhase("error");
    }
  }, [stage]);
  useEffect(() => {
    void reload();
    return () => { requestVersion.current += 1; };
  }, [actorId, refreshVersion, reload]);
  useEffect(() => {
    const timer = window.setInterval(() => {
      const today = currentDay();
      if (today === day.current) return;
      day.current = today;
      void reload();
    }, 60_000);
    return () => window.clearInterval(timer);
  }, [reload]);
  return { items, total, phase, error, reload };
}
